import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { MdContentCopy } from 'react-icons/md';
import * as yup from 'yup';
import { useFormik } from 'formik';
import JoditEditor from 'jodit-react';
import 'react-quill/dist/quill.snow.css';
import {
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  FormControl,
  InputLabel,
} from '@mui/material';

import {
  AUTOMATED_EMAIL_EDITOR_CONFIG,
  MSG_REQUIRED_FIELD,
  LINK_VARIABLES,
  EMAIL_LINK_TRIGGER,
} from '../../utils/contants';
import { apiHeader, BaseURL } from '../../config/apiUrl';
import { Patch, Post } from '../../Axios/AxiosFunctions';
import { Button } from '../../Component/Button/Button';
import { Checkbox } from '../../Component/Checkbox/Checkbox';
import SideBarSkeleton from '../../Component/SideBarSkeleton';
import classes from './AddAutomatedEmail.module.css';

const validationSchema = yup.object().shape({
  trigger: yup.string().required(MSG_REQUIRED_FIELD),
  from: yup.string().required(MSG_REQUIRED_FIELD),
  to: yup.string().required(MSG_REQUIRED_FIELD),
  // cc: yup.string().required(MSG_REQUIRED_FIELD),
  subject: yup.string().required(MSG_REQUIRED_FIELD),
  // link: yup.string().required(MSG_REQUIRED_FIELD),
});

const AddAutomatedEmail = ({ title, editData }) => {
  const editorRef = useRef();
  const navigate = useNavigate();

  const { access_token: accessToken, company_id: companyId } = useSelector(
    (state) => state?.authReducer,
  );
  const baseApiUrl = BaseURL(`automated-email-template`);

  const [emailBodyState, setEmailBodyState] = useState(
    editData?.emailBody || '',
  );

  const [variableSearch, setVariableSearch] = useState('');
  const filteredVariables = useMemo(() => {
    const term = variableSearch.trim().toLowerCase();
    if (!term) return LINK_VARIABLES;
    return LINK_VARIABLES.filter(
      (v) =>
        v.label.toLowerCase().includes(term) ||
        v.value.toLowerCase().includes(term),
    );
  }, [variableSearch]);

  const {
    errors,
    values,
    handleChange,
    touched,
    handleSubmit,
    setFieldValue,
    isSubmitting,
    setSubmitting,
    resetForm,
    handleBlur,
  } = useFormik({
    initialValues: {
      trigger: editData?.trigger || '',
      from: editData?.from || '',
      to: editData?.to || '',
      cc: editData?.cc || '',
      subject: editData?.subject || '',
      isEnabled: editData?.isEnabled || true,
      link: editData?.link || '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (formData) => {
      const body = {
        ...formData,
        emailBody: emailBodyState,
      };

      setSubmitting(true);

      if (editData?._id) {
        const response = await Patch(
          `${baseApiUrl}/${editData?._id}`,
          body,
          apiHeader(accessToken),
        );

        if (response !== undefined) {
          toast.success(`Template Edited Successfully.`);
          setSubmitting(false);
          navigate('/automated-emails');
        }
      } else {
        const response = await Post(
          baseApiUrl,
          body,
          apiHeader(accessToken),
          companyId,
        );

        if (response !== undefined) {
          toast.success(`Template Created Successfully.`);
          setSubmitting(false);
          setEmailBodyState('');
          navigate('/automated-emails');
          resetForm();
        }
      }
    },
  });

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);

      toast.success('Link Copied');
    } catch (error) {
      console.error('Failed to copy text: ', error);
    }
  };

  return (
    <>
      <SideBarSkeleton>
        <div className={classes.container_main}>
          <div className={classes.main_heading}>
            <h4>
              {title
                ? title
                : `${editData?._id ? 'Edit' : 'Add'} Automated Email`}
              {}{' '}
            </h4>
          </div>

          <form
            onSubmit={handleSubmit}
            className={classes.addEmailTemplateModal_main}
          >
            <Row className={classes.input_row}>
              <FormControl
                fullWidth
                error={touched.trigger && Boolean(errors.trigger)}
              >
                <InputLabel id="trigger-label">Trigger *</InputLabel>
                <Select
                  labelId="trigger-label"
                  name="trigger"
                  value={values.trigger}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  label="Trigger *"
                  autoFocus
                >
                  {EMAIL_LINK_TRIGGER.map((trigger) => (
                    <MenuItem key={trigger.value} value={trigger.value}>
                      {trigger.label}
                    </MenuItem>
                  ))}
                </Select>
                {touched.trigger && errors.trigger && (
                  <FormHelperText>{errors.trigger}</FormHelperText>
                )}
              </FormControl>

              <TextField
                label="From email *"
                name="from"
                onChange={handleChange}
                value={values.from}
                error={touched.from && Boolean(errors.from)}
                helperText={touched.from && errors.from}
              />

              <TextField
                label="To email *"
                name="to"
                onChange={handleChange}
                value={values.to}
                error={touched.to && Boolean(errors.to)}
                helperText={touched.to && errors.to}
              />

              <TextField
                label="CC"
                name="cc"
                onChange={handleChange}
                value={values.cc}
                error={touched.cc && Boolean(errors.cc)}
                helperText={touched.cc && errors.cc}
              />

              <TextField
                label="Subject *"
                name="subject"
                onChange={handleChange}
                value={values.subject}
                error={touched.subject && Boolean(errors.subject)}
                helperText={touched.subject && errors.subject}
                className={classes.subject_input}
              />

              <div className={classes.var_section}>
                <TextField
                  size="small"
                  placeholder="Search variables"
                  value={variableSearch}
                  onChange={(e) => setVariableSearch(e.target.value)}
                  fullWidth
                />
                {filteredVariables.length > 0 ? (
                  filteredVariables.map((variable) => (
                    <div className={classes.var_item} key={variable.id}>
                      {variable.label} ({variable.value})
                      <div
                        style={{ cursor: 'pointer' }}
                        onClick={() => copyText(variable.value)}
                      >
                        <MdContentCopy />
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#888', fontSize: 14 }}>
                    No variables match your search.
                  </div>
                )}
              </div>

              <div className={classes.textEditor}>
                <JoditEditor
                  ref={editorRef}
                  value={emailBodyState}
                  config={{
                    ...AUTOMATED_EMAIL_EDITOR_CONFIG,
                    events: {
                      afterInit: (e) => {
                        editorRef.current = e;
                      },
                    },
                  }}
                  onBlur={(value) => setEmailBodyState(value)}
                />
              </div>

              <TextField
                label="Link"
                name="link"
                onChange={handleChange}
                value={values.link}
                error={touched.link && Boolean(errors.link)}
                helperText={touched.link && errors.link}
              />

              <Checkbox
                setValue={(e) => setFieldValue('isEnabled', e === 'Active')}
                value={values.isEnabled ? 'Active' : ''}
                label={'Active'}
              />
            </Row>

            <div className={classes.btn_main}>
              <Button
                type="submit"
                className={classes.btn}
                label={
                  isSubmitting
                    ? 'Submitting...'
                    : editData?._id
                      ? 'Edit Template'
                      : 'Add Template'
                }
                disabled={isSubmitting}
              />
            </div>
          </form>
        </div>
      </SideBarSkeleton>
    </>
  );
};

export default AddAutomatedEmail;
